import { Dexie, type Table } from 'dexie'

/**
 * Lokaler Speicher: Lesecache, Ausgangskorb, Fotowarteschlange, Metadaten.
 *
 * Der wichtigste Unterschied im ganzen Modul:
 *
 *   Der CACHE darf jederzeit verworfen werden – er ist eine Kopie mit
 *   Verfallsdatum, der Server hat alles.
 *   Der AUSGANGSKORB darf NIE automatisch verworfen werden – er enthält
 *   Arbeit, die es sonst nirgends gibt.
 *
 * Deshalb haben beide eine eigene Schemaversion. Ändert sich das Cache-Format,
 * wird der Cache geleert und neu geladen. Der Ausgangskorb bleibt unberührt
 * und bekommt ein bewusst stabiles, flaches Format.
 */

export const CACHE_SCHEMA_VERSION = 1
export const OUTBOX_SCHEMA_VERSION = 1

export type OutboxStatus =
  'local' | 'waiting' | 'sending' | 'success' | 'failed' | 'conflict' | 'unclear'

export type ActionClass = 'A' | 'B'

export interface OutboxEntry {
  /** client_action_id, UUID v7 – zugleich Idempotenzschlüssel und Sortierung. */
  id: string
  createdAt: number
  tenantId: string
  userId: string
  /** z.B. 'customer.create' */
  type: string
  actionClass: ActionClass
  method: 'POST' | 'PATCH' | 'PUT'
  path: string
  payload: unknown
  status: OutboxStatus
  attempts: number
  nextAttemptAt: number
  lastError: string | null
  lastErrorKind: string | null
  serverResult: unknown
  /** Kennungen, die vorher durch müssen (z.B. Kunde vor Projekt). */
  dependsOn: string | null
  /** Lokale Foto-Kennungen, die zu dieser Aktion gehören. */
  attachments: string[]
}

export type PhotoStatus = 'local' | 'presigning' | 'uploading' | 'uploaded' | 'confirmed' | 'failed'

export interface PhotoEntry {
  id: string
  createdAt: number
  tenantId: string
  /** Zu welchem Rapport/Dokument das Foto gehört – gesetzt vom Aufrufer. */
  ownerType: 'rapport' | 'quoterequest'
  ownerId: string
  blob: Blob | null
  bytes: number
  width: number | null
  height: number | null
  status: PhotoStatus
  attempts: number
  nextAttemptAt: number
  storageKey: string | null
  uploadUrl: string | null
  lastError: string | null
}

export interface MetaRow {
  key: string
  value: unknown
}

/** Was im Lesecache liegt. Jede Zeile trägt changeSeq für den Abgleich. */
export interface CacheRow {
  id: string
  changeSeq: number
  [field: string]: unknown
}

/**
 * Dexie 4 exportiert Dexie als Variable, nicht als Klasse – deshalb der
 * dokumentierte Weg über eine Instanz mit Typzusatz statt einer Unterklasse.
 */
export type RaportoDb = Dexie & {
  // Cache – darf jederzeit verworfen werden
  settings: Table<CacheRow, string>
  customers: Table<CacheRow, string>
  projects: Table<CacheRow, string>
  projectMembers: Table<{ id: string; projectId: string; userId: string }, string>
  catalogItems: Table<CacheRow, string>
  rapports: Table<CacheRow, string>
  documents: Table<CacheRow, string>
  // Kein Cache – enthält Arbeit, die es sonst nirgends gibt
  outbox: Table<OutboxEntry, string>
  photos: Table<PhotoEntry, string>
  formentwuerfe: Table<Formentwurf, string>
  meta: Table<MetaRow, string>
}

/**
 * Die örtliche Notfallkopie eines Erfassungsformulars.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * Wozu, wenn doch auf den Server gespeichert wird
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Weil der Server in genau den Augenblicken nicht erreichbar ist, in denen
 * diese Kopie zählt: Keller ohne Empfang, Tunnel, Akku leer, Browser
 * abgestürzt. Der Serverentwurf ist der Hauptweg und hat Vorrang; diese
 * Kopie ist das Netz darunter.
 *
 * Sie gehört NICHT zum Cache und wird beim Mandantenwechsel nicht geleert –
 * sie enthält Arbeit, die es sonst nirgends gibt. Getrennt wird trotzdem,
 * und zwar wie beim Ausgangskorb: Jeder Eintrag trägt Mandant und Person,
 * und gelesen wird nur, was zur angemeldeten Person gehört. Ein Entwurf des
 * einen Betriebs taucht im anderen nicht auf.
 */
/**
 * Wie lange eine örtliche Formularkopie gilt.
 *
 * Steht hier und nicht im Entwurfsspeicher, weil beide Seiten dieselbe Frist
 * brauchen: Der Speicher räumt damit auf und gibt Abgelaufenes nicht heraus,
 * und das Abmelden räumt damit ebenfalls auf. Zwei Zahlen an zwei Orten
 * laufen irgendwann auseinander, und dann hält eine Seite etwas für gültig,
 * was die andere längst entfernt hat.
 */
export const FORMENTWURF_HALTBAR_MS = 24 * 60 * 60 * 1000

export interface Formentwurf {
  /** Die Belegkennung, oder `neu:<art>` solange es noch keine gibt. */
  id: string
  tenantId: string
  userId: string
  art: 'rechnung' | 'offerte'
  /** Der Formularzustand, so wie ihn die Maske wieder einsetzen kann. */
  stand: unknown
  /** Wann zuletzt geändert – entscheidet beim Wiederherstellen. */
  gespeichertAm: number
  /** Stand der Serverfassung, als diese Kopie entstand. */
  serverVersion: number | null
}

export function createDb(name = 'raporto'): RaportoDb {
  const instance = new Dexie(name) as RaportoDb
  instance.version(1).stores({
    settings: 'id',
    customers: 'id, changeSeq, name',
    projects: 'id, changeSeq, customerId, status',
    projectMembers: 'id, projectId, userId',
    catalogItems: 'id, changeSeq, code, name, kind',
    rapports: 'id, changeSeq, projectId, status, workDate',
    documents: 'id, changeSeq, type, status, customerId',
    outbox: 'id, status, nextAttemptAt, createdAt',
    photos: 'id, status, ownerId, nextAttemptAt',
    meta: 'key'
  })
  /*
    Version 2 bringt die Formentwürfe dazu.

    Eine neue Version und kein Ändern der ersten: Dexie führt bestehende
    Datenbanken sonst nicht sauber nach, und auf dem Telefon eines Monteurs
    liegt eine bestehende Datenbank mit Arbeit im Ausgangskorb. Die zweite
    Version lässt alle bisherigen Tabellen unverändert und legt eine dazu.
  */
  instance.version(2).stores({
    formentwuerfe: 'id, tenantId, userId, gespeichertAm'
  })
  return instance
}

export const db = createDb()

/** Tabellen, die zum Cache gehören und gefahrlos geleert werden dürfen. */
const CACHE_TABLES = [
  'settings',
  'customers',
  'projects',
  'projectMembers',
  'catalogItems',
  'rapports',
  'documents'
] as const

export const META_KEYS = {
  cacheSchemaVersion: 'cache_schema_version',
  outboxSchemaVersion: 'outbox_schema_version',
  changeSeq: 'change_seq',
  lastSyncAt: 'last_sync_at',
  currentTenant: 'current_tenant',
  currentUser: 'current_user',
  role: 'role',
  capabilities: 'capabilities',
  offlinePermissionExpiresAt: 'offline_permission_expires_at',
  /*
    Wann zuletzt jemand an diesem Gerät gearbeitet hat.

    Liegt hier und nicht im Speicher der Seite, damit die Sperre ein Neuladen
    überlebt – und ein Neuladen ist das Erste, was jemand versucht, der vor
    einem fremden Bildschirm sitzt.
  */
  letzteBenutzung: 'letzte_benutzung',
  /*
    Seit wann der Bildschirm gesperrt ist – oder nichts.

    Bis Paket 12b wurde die Sperre bei jedem Start aus `letzteBenutzung` neu
    hergeleitet. Das reichte nicht: Im Sperrbildschirm steht ein
    Passwortfeld, jeder Tastendruck galt als Benutzung, und ein Zeichen plus
    Neuladen machte die Sperre zunichte. Gemessen mit `proben/sitzung-probe.mts`.

    Der Zustand steht deshalb jetzt selbst da. Er verschwindet durch das
    richtige Passwort im Sperrbildschirm – oder durch eine vollständige neue
    Anmeldung, siehe `sperrspurLoeschen()`.
  */
  gesperrtSeit: 'gesperrt_seit'
} as const

/**
 * Die Sperrspur dieses Geräts löschen.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Der Produktionsfehler, den das behebt
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Beide Merker liegen in der IndexedDB und überleben damit absichtlich ein
 * Neuladen. Sie überlebten aber auch das Abmelden und die nächste Anmeldung
 * – und niemand löschte sie je ausser dem Sperrbildschirm selbst.
 *
 * Daraus wurde eine Falle, und zwar auf zwei Wegen:
 *
 *   `gesperrtSeit`      Wer den Vorhang sah und sich abmeldete statt zu
 *                       entsperren, trug ihn mit. Die nächste Anmeldung –
 *                       Passwort UND zweiter Faktor – landete sofort wieder
 *                       im Sperrbildschirm, in jedem Tab.
 *
 *   `letzteBenutzung`   Reichte allein. Wer dieses Gerät eine Viertelstunde
 *                       nicht benutzt hatte und sich frisch anmeldete, war
 *                       nach der erfolgreichen Anmeldung gesperrt. Dafür
 *                       brauchte es nicht einmal einen früheren Vorhang.
 *
 * Warum das Löschen hier KEINE Umgehung der Sperre ist: Eine vollständige
 * Anmeldung mit Passwort und zweitem Faktor ist der stärkere Nachweis. Der
 * Sperrbildschirm verlangt ein Passwort; die Anmeldung verlangt dasselbe
 * Passwort und zusätzlich den Code. Wer das gerade bestanden hat, muss es
 * nicht unmittelbar danach noch einmal tun.
 *
 * Die Sperre selbst bleibt unangetastet: Ab dieser Sekunde läuft die
 * Viertelstunde wieder, und wer den Bildschirm stehen lässt, findet ihn
 * verhängt vor.
 */
export async function sperrspurLoeschen(): Promise<void> {
  await db.meta.delete(META_KEYS.gesperrtSeit)
  await db.meta.put({ key: META_KEYS.letzteBenutzung, value: Date.now() })
}

export async function getMeta<T>(key: string, fallback: T): Promise<T> {
  const row = await db.meta.get(key)
  return row ? (row.value as T) : fallback
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value })
}

/** Cache leeren – der Ausgangskorb und die Fotos bleiben. */
export async function clearCache(): Promise<void> {
  await db.transaction(
    'rw',
    CACHE_TABLES.map((t) => db.table(t)),
    async () => {
      for (const t of CACHE_TABLES) await db.table(t).clear()
    }
  )
  await setMeta(META_KEYS.changeSeq, 0)
  await setMeta(META_KEYS.lastSyncAt, null)
}

/**
 * Beim Start prüfen, ob das Cache-Format noch passt. Passt es nicht, wird der
 * Cache verworfen und beim nächsten Abgleich neu geladen.
 *
 * Der Ausgangskorb wird dabei ausdrücklich nicht angefasst. Stimmt seine
 * Version einmal nicht, ist das ein Fall für eine echte Migration, nicht für
 * ein Leeren – dort liegt Arbeit, die es sonst nirgends gibt.
 */
export async function ensureSchema(): Promise<{ cacheDropped: boolean; outboxKept: number }> {
  const cacheVersion = await getMeta<number>(META_KEYS.cacheSchemaVersion, 0)
  const outboxCount = await db.outbox.count()
  let cacheDropped = false

  if (cacheVersion !== CACHE_SCHEMA_VERSION) {
    await clearCache()
    await setMeta(META_KEYS.cacheSchemaVersion, CACHE_SCHEMA_VERSION)
    cacheDropped = true
  }

  const outboxVersion = await getMeta<number>(META_KEYS.outboxSchemaVersion, 0)
  if (outboxVersion !== OUTBOX_SCHEMA_VERSION) {
    // Nur vermerken. Ein Formatwechsel im Ausgangskorb braucht eine echte
    // Migration an dieser Stelle – niemals ein Leeren.
    await setMeta(META_KEYS.outboxSchemaVersion, OUTBOX_SCHEMA_VERSION)
  }

  return { cacheDropped, outboxKept: outboxCount }
}

/**
 * Den Zwischenspeicher an genau einen Betrieb und eine Person binden.
 *
 * ---------------------------------------------------------------------------
 * Der Fehler, den diese Funktion behebt
 * ---------------------------------------------------------------------------
 *
 * Der Zwischenspeicher liegt im Browser und überlebt Abmelden, Neuladen und
 * das Schliessen des Fensters. Er trug bis hierher nirgends, WEM er gehört.
 *
 * Was dabei herauskommt, ist kein Schönheitsfehler: Wer sich im selben
 * Browser einen zweiten Betrieb einrichtet, hat den Bestand des ersten noch
 * in der IndexedDB – Firmenangaben, Kunden, Baustellen, Katalog. Und weil der
 * Abgleich nur holt, was NEUER ist als der gespeicherte Zählstand, und der
 * gespeicherte Zählstand vom alten Betrieb stammt und höher liegt als alles,
 * was der neue je hatte, kommt beim neuen Betrieb nichts nach. Der Bildschirm
 * zeigt dann den fremden Betrieb, und niemand bekommt eine Fehlermeldung.
 *
 * Der Server war nie das Problem – er hat noch nie Daten eines fremden
 * Mandanten herausgegeben. Undicht war der Browser.
 *
 * ---------------------------------------------------------------------------
 * Was hier passiert
 * ---------------------------------------------------------------------------
 *
 * Stimmen Mandant UND Person mit dem überein, wofür der Zwischenspeicher
 * gefüllt wurde, bleibt alles stehen. Stimmt eines von beiden nicht, wird er
 * geleert, bevor irgendetwas angezeigt oder abgeglichen wird.
 *
 * Auch die Person zählt, nicht nur der Betrieb: Chef und Monteur sehen
 * verschiedene Ausschnitte desselben Mandanten – der Monteur nur seine
 * Baustellen, und Preise gar nicht. Bliebe der Zwischenspeicher des Chefs
 * liegen, sähe der Monteur auf demselben Gerät Preise, die er nicht sehen
 * darf.
 *
 * Der Ausgangskorb wird NICHT geleert. Dort liegt Arbeit, die es sonst
 * nirgends gibt – ein Rapport von der Baustelle, der noch nicht oben ist.
 * Dass er trotzdem nicht im falschen Betrieb landet, sorgt der Versand:
 * er nimmt nur Einträge, deren Mandant zum angemeldeten passt.
 */
export async function bindeAn(
  tenantId: string,
  userId: string
): Promise<{ gewechselt: boolean; fremdeAusgaenge: number }> {
  const bisherMandant = await getMeta<string | null>(META_KEYS.currentTenant, null)
  const bisherPerson = await getMeta<string | null>(META_KEYS.currentUser, null)
  const gewechselt = bisherMandant !== tenantId || bisherPerson !== userId

  if (gewechselt) {
    await clearCache()
    /*
      Die örtlichen Formularentwürfe eines anderen Paares verschwinden hier.

      Sie tragen zwar Mandant und Person, und `hole()` gibt einen fremden
      Eintrag nie heraus – aber liegen bleiben müssen sie deshalb nicht. In
      einem Entwurf steht der Freitext eines Kundenauftrags; auf einem Gerät,
      an dem jetzt jemand anders arbeitet, hat er nichts mehr zu suchen.

      Nur die fremden, nicht die eigenen: Wer den Betrieb wechselt und
      zurückkommt, findet seinen angefangenen Beleg wieder.
    */
    await loescheFremdeFormentwuerfe(tenantId, userId)
    await setMeta(META_KEYS.currentTenant, tenantId)
    await setMeta(META_KEYS.currentUser, userId)
  }

  /*
    Wie viel Arbeit eines anderen Betriebs noch im Korb liegt. Niemand darf
    sie senden, aber jemand muss es wissen – ein stiller Stapel, der nie
    losgeht, ist am Ende derselbe Datenverlust wie ein gelöschter.

    Gezählt wird alles, was nicht durch ist, und nicht nur das
    Sendebereite: ein Eintrag, der beim letzten Versuch an einem Netzfehler
    gescheitert ist, steht auf «failed» und ist trotzdem unerledigte Arbeit.
    Ihn nicht mitzuzählen hiesse, dem Benutzer zu sagen, es liege nichts mehr
    an – während sein Rapport im Korb liegt.
  */
  const alle = await db.outbox.toArray()
  const fremdeAusgaenge = alle.filter(
    (e) => e.tenantId !== tenantId && e.status !== 'success'
  ).length

  return { gewechselt, fremdeAusgaenge }
}

/**
 * Örtliche Formularentwürfe, die nicht diesem Paar gehören, entfernen.
 *
 * Fehlertolerant wie der ganze Entwurfsspeicher: Eine gesperrte oder fehlende
 * IndexedDB darf das Anmelden nicht aufhalten.
 */
async function loescheFremdeFormentwuerfe(tenantId: string, userId: string): Promise<void> {
  try {
    const alle = await db.formentwuerfe.toArray()
    const fremde = alle
      .filter((e) => e.tenantId !== tenantId || e.userId !== userId)
      .map((e) => e.id)
    if (fremde.length > 0) await db.formentwuerfe.bulkDelete(fremde)
  } catch {
    /* Kein Platz, privater Modus, gesperrte Ablage: dann eben nicht. */
  }
}

/**
 * Beim Abmelden: Cache weg, Metadaten weg. Der Ausgangskorb bleibt, damit
 * kein Rapport verlorengeht, nur weil jemand sich abgemeldet hat.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Dieselbe Regel gilt für die Formularentwürfe – und zwar seit einer
 * Korrektur
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Zwischenzeitlich wurde die Tabelle beim Abmelden vollständig geleert. Das
 * sah nach Sauberkeit aus und war ein Datenverlust: Ein angefangener Beleg
 * OHNE Kunden lässt sich serverseitig gar nicht ablegen – `documents`
 * verlangt einen Kunden und hat einen Fremdschlüssel darauf. Für genau diese
 * Anfänge ist die örtliche Kopie nicht eine Zweitschrift, sondern das
 * Einzige, was es gibt. Wer eine halbe Stunde Positionen erfasst, den Kunden
 * noch nicht gewählt hat und sich abmeldet, fing von vorne an.
 *
 * Deshalb gilt hier wie beim Ausgangskorb: **Was es sonst nirgends gibt,
 * wird beim Abmelden nicht weggeworfen.**
 *
 * Und das ist kein Schlupfloch, sondern präzise: Ein Eintrag verschwindet,
 * sobald der Server denselben Stand bestätigt hat (`vergiss` in
 * `useEntwurfsSicherung`). Was beim Abmelden noch liegt, ist also
 * definitionsgemäss unbestätigte Arbeit. Begrenzt bleibt sie trotzdem –
 * durch alles andere, was der Entwurfsspeicher ohnehin tut:
 *
 *   · Inhalt: nur der Formularstand, höchstens 100 000 Zeichen.
 *   · Zeit: 24 Stunden, geprüft beim Aufräumen UND beim Lesen.
 *   · Person und Betrieb: jede Abfrage filtert auf beide, und beim Wechsel
 *     verschwindet alles Fremde (`bindeAn` oben).
 *
 * Aufgeräumt wird beim Abmelden trotzdem: Abgelaufenes geht weg, und zwar
 * sofort statt erst beim nächsten Öffnen der Maske.
 */
export async function clearOnLogout(): Promise<void> {
  await clearCache()
  try {
    const grenze = Date.now() - FORMENTWURF_HALTBAR_MS
    await db.formentwuerfe.where('gespeichertAm').below(grenze).delete()
  } catch {
    /* siehe oben */
  }
  await db.photos.where('status').anyOf('uploaded', 'confirmed').delete()
  for (const key of [
    META_KEYS.currentTenant,
    META_KEYS.currentUser,
    META_KEYS.role,
    META_KEYS.capabilities,
    META_KEYS.offlinePermissionExpiresAt,
    /*
      Die Sperrspur gehört der beendeten Sitzung.

      Blieb sie liegen, nahm der Nächste, der sich an diesem Gerät anmeldet,
      den Vorhang des Vorgängers mit – und hatte keinen Weg hinaus, weil der
      Sperrbildschirm bis zu diesem Paket keinen Abmeldeknopf hatte. Genau
      das war der gemeldete Produktionsfehler.
    */
    META_KEYS.gesperrtSeit,
    META_KEYS.letzteBenutzung
  ]) {
    await db.meta.delete(key)
  }
}
